import type * as SentryTypes from '@sentry/vue'
import type { UserProfile } from '~/types/user'

let Sentry: typeof SentryTypes | null = null

export const useSentryService = () => {
  async function init(dsn: string, environment: string) {
    if (import.meta.server) return

    Sentry = await import('@sentry/vue')

    const { vueApp } = useNuxtApp()

    Sentry.init({
      app: [vueApp],
      dsn,
      environment,
      tracesSampleRate: 0.01,
      integrations: [
        Sentry.vueIntegration({
          tracingOptions: {
            trackComponents: true,
            hooks: ['activate', 'mount', 'update'],
            timeout: 2000
          }
        })
      ],
      beforeSend(event, hint) {
        const error = hint?.originalException as
          | { isAxiosError?: boolean; code?: string; response?: { status?: number } }
          | undefined

        if (error?.isAxiosError) {
          const status = error.response?.status ?? 0
          // 5xx / 0 = backend or transient connectivity, 401 = handled by refresh/logout
          if (status === 0 || status === 401 || status >= 500) return null
          if (
            error.code === 'ERR_NETWORK' ||
            error.code === 'ECONNABORTED' ||
            error.code === 'ETIMEDOUT'
          ) {
            return null
          }
        }

        if (event.exception) {
          console.error(`[Exception handled by Sentry]: (${hint.originalException})`, { event, hint })
        }
        return event
      }
    })
  }

  function setUser(user: UserProfile) {
    Sentry?.setUser({ id: user.id.toString(), email: user.email })
  }

  function unsetUser() {
    Sentry?.setUser(null)
  }

  function captureException(...args: Parameters<typeof SentryTypes.captureException>) {
    Sentry?.captureException(...args)
  }

  function setContext(...args: Parameters<typeof SentryTypes.setContext>) {
    Sentry?.setContext(...args)
  }

  function setTag(...args: Parameters<typeof SentryTypes.setTag>) {
    Sentry?.setTag(...args)
  }

  function addBreadcrumb(...args: Parameters<typeof SentryTypes.addBreadcrumb>) {
    Sentry?.addBreadcrumb(...args)
  }

  return {
    init,
    setUser,
    unsetUser,
    captureException,
    setContext,
    setTag,
    addBreadcrumb
  }
}
